/*
 * ===============================================================================================================
 *                                Copyright 2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Box,
  CircularProgress,
  Checkbox,
  Chip,
  ClickAwayListener,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  TextField,
  Autocomplete
} from '@mui/material';
import { LuiPagination } from '@jda/lui-common-component-library-mui5';
import _ from 'lodash';
import { Location } from '../../models/Locations/Locations';
import CloseIcon from '@mui/icons-material/Close';
import { getLocations } from '../../services/locations';
import { useStyles } from './styles';
import { ALL, NO_OPTIONS } from './constants';

const selectAllOption = { id: '*', displayName: ALL } as Location;

interface LocationSelectProps {
  id: string;
  locationType?: string;
  value: Location[];
  disabledLocationIds?: string[];
  isLocationDisabled?: (location: Location) => boolean;
  onChange: (locations: Location[]) => void;
}

const LocationSelect = ({
  id,
  locationType,
  value,
  disabledLocationIds = [],
  isLocationDisabled,
  onChange
}: LocationSelectProps) => {
  const [searchValue, setSearchValue] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [locationResponse, setLocationResponse] = useState<any>();
  const [isLoading, setIsLoading] = useState(Boolean(locationType));
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef();

  useEffect(() => {
    if (!locationType) {
      return;
    }

    let ignore = false;

    setIsLoading(true);
    getLocations({
      locationType,
      pageNumber,
      ...(searchValue && { filterByLocationName: searchValue })
    })
      .then(locationResponse => {
        if (!ignore) {
          setLocationResponse(locationResponse);
        }
      })
      .finally(() => {
        if (!ignore) {
          setIsLoading(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [locationType, pageNumber, searchValue]);

  const { classes } = useStyles();

  const isDisabled = useCallback(
    (location: Location) => disabledLocationIds.includes(location.id) || (isLocationDisabled?.(location) ?? false),
    [disabledLocationIds, isLocationDisabled]
  );

  const locationOptionsDecorated: { value: Location; isSelected: boolean; isDisabled: boolean }[] = useMemo(() => {
    if (!locationResponse) {
      return [];
    }

    const selectedLocationIdSet = new Set(value.map(location => location.id));

    return locationResponse.data.map((location: Location) => ({
      value: location,
      isSelected: selectedLocationIdSet.has(location.id),
      isDisabled: isDisabled(location)
    }));
  }, [locationResponse, value, isDisabled]);

  const enabledLocationOptions = useMemo(() => locationOptionsDecorated.filter(({ isDisabled }) => !isDisabled), [
    locationOptionsDecorated
  ]);

  const isSelectAllDisabled = enabledLocationOptions.length === 0;

  const isAllSelected = useMemo(
    () => !isSelectAllDisabled && enabledLocationOptions.every(({ isSelected }) => isSelected),
    [isSelectAllDisabled, enabledLocationOptions]
  );

  const getOptionLabel = (option: Location) => option.displayName;

  const handleChange = (_event: any, value: Location[]) => {
    onChange(value);
  };

  const handleInputChange = (_event: any, value: string) => {
    setSearchValue(value);
    setPageNumber(1);
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = (_event: any, reason: string) => {
    if (['toggleInput', 'escape'].includes(reason)) {
      setIsOpen(false);
    }
  };

  const PopoverContent = ({ className }: { className?: string }) => (
    <ClickAwayListener
      onClickAway={event => {
        if (event.target !== inputRef.current) {
          setIsOpen(false);
        }
      }}
    >
      <Paper className={className}>
        {locationOptionsDecorated.length > 0 ? (
          <>
            <List className={classes.locationCheckboxList}>
              {[
                { value: selectAllOption, isSelected: isAllSelected, isDisabled: isSelectAllDisabled },
                ...locationOptionsDecorated
              ].map(({ value: location, isSelected, isDisabled }) => {
                const labelId = `location-option-label-${location.id}`;

                return (
                  <ListItem
                    key={location.id}
                    button={true}
                    role={undefined}
                    disabled={isDisabled}
                    className={classes.locationCheckboxOption}
                    onClick={() => {
                      const optionValue =
                        location.id === '*' ? enabledLocationOptions.map(({ value }) => value) : [location];

                      onChange(
                        isSelected
                          ? _.differenceBy(value, optionValue, location => location.id)
                          : _.unionBy(value, optionValue, location => location.id)
                      );
                    }}
                  >
                    <ListItemIcon className={classes.locationCheckboxPrimaryAction}>
                      <Checkbox
                        checked={isSelected}
                        tabIndex={-1}
                        disableRipple={true}
                        color="primary"
                        inputProps={{ 'aria-labelledby': labelId }}
                      />
                    </ListItemIcon>
                    <ListItemText id={labelId} primary={location.displayName} />
                  </ListItem>
                );
              })}
            </List>
            <Box className={classes.locationPagination}>
              <LuiPagination
                totalRows={locationResponse.metadata.totalCount}
                page={pageNumber - 1}
                rowsPerPage={locationResponse.metadata.pageSize}
                onPageChange={(_event, page) => {
                  setPageNumber(page + 1);
                }}
              />
            </Box>
          </>
        ) : (
          <Box className={classes.locationNoOptions}>{NO_OPTIONS}</Box>
        )}
      </Paper>
    </ClickAwayListener>
  );

  return (
    <Autocomplete
      id={id}
      multiple={true}
      value={value}
      onChange={handleChange}
      options={value}
      open={isOpen}
      getOptionLabel={getOptionLabel}
      onInputChange={handleInputChange}
      onOpen={handleOpen}
      onClose={handleClose}
      classes={{ root: classes.autocompleteMultiple }}
      renderInput={params => (
        <TextField
          {...params}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading && <CircularProgress size={20} />}
                {params.InputProps.endAdornment}
              </>
            )
          }}
          inputRef={inputRef}
        />
      )}
      renderTags={(value, getTagProps) =>
        value.map((option, index) =>
          isDisabled(option) ? null : (
            <Chip label={option.displayName} deleteIcon={<CloseIcon />} {...getTagProps({ index })} />
          )
        )
      }
      PaperComponent={PopoverContent}
    />
  );
};

export default LocationSelect;
